"use server";

import { z } from "zod";
import { headers } from "next/headers";
import { admin } from "./db";
import { logEvent } from "./events";
import { leadVerdict, leadCounts } from "./rate-limit";
import type { ActionResult } from "./actions";

// ─────────────────────────────────────────────────────────────────────────────
// lib/lead-actions.ts — the homepage email gate (§19).
//
// The gate captures the visitor's email BEFORE the builder, tagged with the
// category they clicked. This email is UNVERIFIED — it is a lead, not an
// account. The create flow still runs the OTP gate; this only means the visitor
// doesn't type their address twice.
//
// Every write is a server action via the service role (CLAUDE.md). No client
// ever touches the leads table.
// ─────────────────────────────────────────────────────────────────────────────

// Categories the showcase can tag a lead with. 'any' = a generic CTA.
// The ruled 10 categories (category ruling, 1 Aug 2026, revised 3 Aug 2026 to
// retire digital-card, extended 3 Aug 2026 with app-landing) + legacy keys.
// "birthday" and "digital-card" stay for existing lead rows; new leads use
// the ruled set.
const CATEGORIES = [
  "any",
  "shop",
  "shop-services",
  "restaurant",
  "clinic",
  "coaching",
  "portfolio",
  "digital-card",
  "wedding",
  "birthday",
  "real-estate",
  "event-vendors",
  "app-landing",
] as const;

const SOURCES = [
  "nav",
  "hero",
  "template",
  "sticky",
  "final",
  "pricing-free",
] as const;

const CaptureSchema = z.object({
  // Mirrors the reference's inline regex, plus a length cap.
  email: z.string().trim().toLowerCase().email().max(254),
  category: z.enum(CATEGORIES).default("any"),
  source: z.enum(SOURCES).default("hero"),
  // True when the clicked category has no template built yet — the lead is a
  // waitlist signup and must NOT be promised a builder.
  waitlist: z.boolean().default(false),
});

async function clientIp(): Promise<string | null> {
  const h = await headers();
  return h.get("x-forwarded-for")?.split(",")[0]?.trim() ?? null;
}

export async function captureLead(
  input: unknown,
): Promise<ActionResult<{ waitlist: boolean }>> {
  const parsed = CaptureSchema.safeParse(input);
  if (!parsed.success) {
    return { ok: false, error: "Please enter a valid email address." };
  }
  const { email, category, source, waitlist } = parsed.data;

  const ip = await clientIp();
  const verdict = leadVerdict(await leadCounts(ip));
  if (!verdict.ok) return { ok: false, error: verdict.message };

  // A repeat submission of the same email+category (double-click, resubmit)
  // collapses into a no-op — the leads table is what the funnel is measured
  // on, and the visitor still sees success either way.
  const { data, error } = await admin()
    .from("leads")
    .upsert(
      { email, category, source, waitlist },
      { onConflict: "email,category", ignoreDuplicates: true },
    )
    .select("id");

  if (error) {
    console.error("captureLead failed:", error.message);
    return { ok: false, error: "Something went wrong. Please try again." };
  }

  // Every meaningful action logs to events — a lead must read as a story.
  // Only for the row that actually landed; a collapsed duplicate isn't a
  // second lead and shouldn't count as a second one in the daily report.
  if (data && data.length > 0) {
    await logEvent({
      action: "lead_captured",
      meta: { email, category, source, waitlist, ip },
    });
  }

  return { ok: true, data: { waitlist } };
}
